import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getRequest, postRequest, patchRequest, deleteRequest } from '../../services/api/apiService';

// Page "Skills IA" (menu Connecteurs) — bibliothèque de stratégies de media
// buying qu'Alex (chatbot) consulte via function calling (list_skills /
// get_skill, voir chatService.js côté serveur). 63 skills experts intégrés
// (repo ai-media-buying-skills : Meta Ads / TikTok Ads / Google LSA) +
// skills personnalisés de l'utilisateur. Activer/désactiver un skill change
// ce qu'Alex voit — la désactivation d'un skill intégré n'affecte que
// l'utilisateur courant (préférence par utilisateur côté serveur).

const PLATFORM_META = {
  'meta-ads':   { label: 'Meta Ads',   badge: 'bg-blue-100 text-blue-700' },
  'tiktok-ads': { label: 'TikTok Ads', badge: 'bg-pink-100 text-pink-700' },
  'google-lsa': { label: 'Google LSA', badge: 'bg-yellow-100 text-yellow-700' },
  other:        { label: 'Autre',      badge: 'bg-gray-100 text-gray-600' },
};

const CATEGORY_LABELS = {
  strategy: 'Stratégie', diagnostics: 'Diagnostics', creative: 'Créatif',
  reporting: 'Reporting', launch: 'Lancement', funnel: 'Funnel',
  operations: 'Opérations', disputes: 'Litiges', custom: 'Personnalisé', other: 'Autre',
};

const PLATFORM_TABS = [
  { value: 'all', label: 'Toutes' },
  { value: 'meta-ads', label: 'Meta' },
  { value: 'tiktok-ads', label: 'TikTok' },
  { value: 'google-lsa', label: 'Google LSA' },
  { value: 'other', label: 'Autres' },
];

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    title={checked ? 'Actif — Alex peut utiliser ce skill' : 'Inactif — masqué pour Alex'}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-gray-300'} ${disabled ? 'opacity-50' : ''}`}
  >
    <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${checked ? 'translate-x-4.5 ml-0.5' : 'translate-x-0.5'}`} style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }} />
  </button>
);

const EMPTY_FORM = { title: '', platform: 'other', category: 'custom', content: '' };

const SkillsManager = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformTab, setPlatformTab] = useState('all');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [viewSkill, setViewSkill] = useState(null); // skill complet (avec content)
  const [loadingView, setLoadingView] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getRequest('/api/skills')
      .then((data) => setSkills(data.skills || []))
      .catch(() => toast.error('Impossible de charger les skills.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const set = new Set(skills.map((s) => s.category));
    return ['all', ...[...set].sort()];
  }, [skills]);

  const filtered = useMemo(() => skills.filter((s) => {
    if (platformTab !== 'all' && s.platform !== platformTab) return false;
    if (category !== 'all' && s.category !== category) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${s.title} ${s.summary || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [skills, platformTab, category, search]);

  const activeCount = skills.filter((s) => s.isActive).length;
  const customCount = skills.filter((s) => s.source === 'custom').length;

  const handleToggle = async (skill) => {
    setTogglingId(skill.id);
    try {
      await patchRequest(`/api/skills/${skill.id}`, { isActive: !skill.isActive });
      setSkills((prev) => prev.map((s) => (s.id === skill.id ? { ...s, isActive: !s.isActive } : s)));
    } catch {
      toast.error("Impossible de changer l'état du skill.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleView = async (skill) => {
    setLoadingView(true);
    setViewSkill({ ...skill, content: null });
    try {
      const full = await getRequest(`/api/skills/${skill.id}`);
      setViewSkill(full);
    } catch {
      toast.error('Impossible de charger le contenu du skill.');
      setViewSkill(null);
    } finally {
      setLoadingView(false);
    }
  };

  const handleDelete = async (skill) => {
    if (!window.confirm(`Supprimer le skill "${skill.title}" ?`)) return;
    try {
      await deleteRequest(`/api/skills/${skill.id}`);
      setSkills((prev) => prev.filter((s) => s.id !== skill.id));
      setViewSkill(null);
      toast.success('Skill supprimé.');
    } catch {
      toast.error('Suppression impossible.');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Titre et contenu sont requis.');
      return;
    }
    setSaving(true);
    try {
      await postRequest('/api/skills', form);
      toast.success('Skill ajouté — Alex peut maintenant l\'utiliser.');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Ajout impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const r = await postRequest('/api/skills/import', {});
      toast.success(`Librairie synchronisée : ${r.created} créé(s), ${r.updated} actualisé(s).`);
      load();
    } catch {
      toast.error('Import impossible.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-3 md:p-6 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">Skills IA — Stratégies d'Alex</h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Méthodologies de media buying qu'Alex applique à vos vraies données (diagnostics, scaling, créatif, funnel…).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleImport}
            disabled={importing}
            className="px-4 py-2 text-xs font-semibold rounded-xl border border-[#221C51]/20 text-[#221C51] hover:bg-gray-50 transition disabled:opacity-50"
          >
            {importing ? 'Synchronisation…' : '⟳ Resynchroniser la librairie'}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-[#221C51] text-white hover:bg-[#2d2570] transition shadow-sm"
          >
            + Ajouter un skill
          </button>
        </div>
      </div>

      {/* Stat chips */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-3 py-1.5 rounded-full bg-indigo-50 text-[#221C51] font-semibold">{skills.length} skills</span>
        <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{activeCount} actifs</span>
        <span className="px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 font-semibold">{customCount} personnalisés</span>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="flex items-center bg-gray-100 rounded-xl p-1 text-xs font-semibold gap-0.5 overflow-x-auto">
          {PLATFORM_TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setPlatformTab(t.value)}
              className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all ${platformTab === t.value ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-xs font-semibold bg-gray-100 rounded-xl px-3 py-2 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20"
        >
          {categories.map((c) => (
            <option key={c} value={c}>{c === 'all' ? 'Toutes catégories' : (CATEGORY_LABELS[c] || c)}</option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un skill…"
          className="flex-1 text-xs bg-gray-100 rounded-xl px-3 py-2 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Chargement des skills…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">
          Aucun skill ne correspond à ces filtres.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50 overflow-hidden">
          {filtered.map((s) => {
            const meta = PLATFORM_META[s.platform] || PLATFORM_META.other;
            return (
              <div key={s.id} className={`px-4 md:px-5 py-3.5 flex items-start gap-3 hover:bg-gray-50 transition ${!s.isActive ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleView(s)}>
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-sm font-semibold text-[#221C51]">{s.title}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.badge}`}>{meta.label}</span>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{CATEGORY_LABELS[s.category] || s.category}</span>
                    {s.source === 'custom' && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">perso</span>
                    )}
                  </div>
                  {s.summary && <p className="text-xs text-gray-500 line-clamp-2">{s.summary}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 pt-1">
                  {s.source === 'custom' && (
                    <button onClick={() => handleDelete(s)} className="text-xs text-red-400 hover:text-red-600" title="Supprimer">✕</button>
                  )}
                  <Toggle checked={s.isActive} disabled={togglingId === s.id} onChange={() => handleToggle(s)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-gray-400 text-center">
        Alex consulte automatiquement les skills actifs quand vous lui posez une question de stratégie, d'analyse ou de diagnostic dans l'AI Launcher.
      </p>

      {/* ── View modal ── */}
      {viewSkill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setViewSkill(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-[#221C51]">{viewSkill.title}</h3>
                <p className="text-[11px] text-gray-400">
                  {(PLATFORM_META[viewSkill.platform] || PLATFORM_META.other).label} · {CATEGORY_LABELS[viewSkill.category] || viewSkill.category}
                </p>
              </div>
              <button onClick={() => setViewSkill(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            <div className="p-5 overflow-y-auto text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
              {loadingView ? 'Chargement…' : viewSkill.content}
            </div>
          </div>
        </div>
      )}

      {/* ── Add modal ── */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAdd(false)}>
          <form
            onSubmit={handleAdd}
            className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#221C51]">Ajouter un skill personnalisé</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Titre (ex: Ma stratégie de scaling e-commerce)"
                className="w-full text-sm bg-gray-100 rounded-xl px-3 py-2.5 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20"
              />
              <div className="flex gap-2">
                <select
                  value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  className="flex-1 text-xs font-semibold bg-gray-100 rounded-xl px-3 py-2.5 border-none focus:outline-none"
                >
                  <option value="other">Toutes plateformes</option>
                  <option value="meta-ads">Meta Ads</option>
                  <option value="tiktok-ads">TikTok Ads</option>
                  <option value="google-lsa">Google LSA</option>
                </select>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="flex-1 text-xs font-semibold bg-gray-100 rounded-xl px-3 py-2.5 border-none focus:outline-none"
                >
                  {Object.entries(CATEGORY_LABELS).filter(([k]) => k !== 'other').map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={"Décrivez la stratégie/méthodologie en markdown :\n\n# Titre\n## Quand l'utiliser\n## Workflow d'analyse\n1. …\n2. …\n## Bonnes pratiques"}
                rows={12}
                className="w-full text-xs bg-gray-100 rounded-xl px-3 py-2.5 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20 font-mono"
              />
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-xs font-semibold rounded-xl text-gray-500 hover:bg-gray-50">
                Annuler
              </button>
              <button type="submit" disabled={saving} className="px-5 py-2 text-xs font-semibold rounded-xl bg-[#221C51] text-white hover:bg-[#2d2570] disabled:opacity-50">
                {saving ? 'Ajout…' : 'Ajouter le skill'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SkillsManager;
